"use client"
import { useEffect, useRef } from "react"

interface GoogleAnalyticsProps {
  measurementId: string
  nonce?: string
}

// Google Analytics 4 (gtag.js) — loads on every page. The gtag.js script is inserted by this
// nonce'd module (CSP 'strict-dynamic' trusts it, and the collect endpoints are allow-listed in
// connect-src/img-src) and tagged data-cmp-ab="off" so the consentmanager auto-blocker does not
// strip it — same pattern as MetaPixel. gtag is exposed on window so conversion events can fire it.
//
// The 168 KB gtag bundle used to be requested the moment React hydrated, competing with the
// hero image for bandwidth on a phone (it showed up in every LCP trace of the 2026-09-01
// audit). It now waits for the window `load` event and then an idle slot. `gtag` and the
// dataLayer are stubbed synchronously, so any conversion event fired before the script
// arrives is queued, not lost — and the page_view still counts every visit.
export function GoogleAnalytics({ measurementId, nonce = "" }: GoogleAnalyticsProps) {
  const loaded = useRef(false)

  useEffect(() => {
    if (loaded.current) return
    loaded.current = true

    const w = window as unknown as {
      dataLayer: unknown[]
      gtag: (...args: unknown[]) => void
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
    }
    w.dataLayer = w.dataLayer || []
    const gtag = (...args: unknown[]) => {
      w.dataLayer.push(args)
    }
    w.gtag = gtag
    gtag("js", new Date())
    gtag("config", measurementId)

    let cancelled = false
    const inject = () => {
      if (cancelled || document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) return
      const s = document.createElement("script")
      s.async = true
      s.src = "https://www.googletagmanager.com/gtag/js?id=" + measurementId
      s.setAttribute("data-cmp-ab", "off")
      if (nonce) s.nonce = nonce
      document.head.appendChild(s)
    }
    const whenIdle = () => {
      if (typeof w.requestIdleCallback === "function") w.requestIdleCallback(inject, { timeout: 4000 })
      else setTimeout(inject, 1500)
    }
    if (document.readyState === "complete") whenIdle()
    else window.addEventListener("load", whenIdle, { once: true })

    return () => {
      cancelled = true
      window.removeEventListener("load", whenIdle)
    }
  }, [measurementId, nonce])

  return null
}
